/*
 * Promotional code eligibility cap.
 *
 * Bulk Discounts Now (BDN) applies quantity-break pricing client-side and
 * validates promotional codes against its own server, then checks out via a
 * draft order. Shopify never sees the quantity-break discount or the code, so
 * this rule cannot be enforced with a Shopify discount or validation function.
 *
 * This guard rebuilds the post-quantity-break merchandise subtotal from the
 * same three inputs BDN uses -- the Shopify cart, BDN's tier configuration and
 * BDN's product/collection lookup -- and refuses the promotional code above the
 * cap. Shipping, taxes and the promotional code itself are excluded.
 *
 * It deliberately never touches the checkout button: BDN binds its own handler
 * there to build the draft order, and pre-empting it would silently drop the
 * quantity-break pricing.
 */
(function () {
  'use strict';

  // Codes are allowed at or below this merchandise subtotal, in cents.
  var CAP_CENTS = 50000;
  var MESSAGE = 'This promotional code is available only on merchandise orders of $500 or less.';

  var BDN_FALLBACK_HOST = 'https://app.bulkdiscountsnow.com';
  var CODE_INPUT = '#discount-code-field';
  var APPLY_BUTTON = '#submit-discount-code, .apply-discount-code';
  var ERROR_CLASS = 'qb-invalid-discount-code promo-cap-error';

  // subtotal stays null until we have a trustworthy number. Every decision below
  // treats null as "don't interfere" so an outage here can never block a sale.
  var state = { subtotal: null, token: null, settings: null, enforcing: false };

  function bdnHost() {
    if (window.qb && window.qb.serverAddress) return window.qb.serverAddress;
    return BDN_FALLBACK_HOST;
  }

  function shopDomain() {
    if (window.Shopify && window.Shopify.shop) return window.Shopify.shop;
    return '';
  }

  function getJSON(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  function loadSettings() {
    if (state.settings) return Promise.resolve(state.settings);
    return getJSON(bdnHost() + '/widget/shop_settings?shop=' + encodeURIComponent(shopDomain()))
      .then(function (s) { state.settings = s; return s; });
  }

  function loadCollectionIds(productIds) {
    if (!productIds.length) return Promise.resolve({});
    return getJSON(bdnHost() + '/product/collection_ids?shop=' + encodeURIComponent(shopDomain()) +
      '&product_ids=' + productIds.join(','))
      .then(function (j) { return (j && j.data) || {}; });
  }

  /*
   * BDN's own rounding, reproduced exactly. Percentages are truncated to whole
   * points and the line total is floored twice, so re-deriving it any other way
   * drifts by a cent or two on larger lines.
   */
  function discountCents(unitPrice, quantity, percent) {
    var value = Math.floor(100 * percent);
    return Math.floor(Math.floor(unitPrice * value * quantity) / 100);
  }

  function groupMatchesLine(group, lineCollectionIds) {
    if (group.type !== 'CollectionDiscount') return false;
    var wanted = (group.collections || []).map(function (c) { return String(c.shopify_collection_id); });
    for (var i = 0; i < lineCollectionIds.length; i++) {
      if (wanted.indexOf(String(lineCollectionIds[i])) !== -1) return true;
    }
    return false;
  }

  // Highest tier whose minimum line quantity the line meets.
  function bestTierFor(group, quantity) {
    var best = null;
    var tiers = group.discount_tiers || [];
    for (var i = 0; i < tiers.length; i++) {
      var t = tiers[i];
      if (t.type !== 'MinimumLineItemQuantityTier') continue;
      if (t.min_line_item_quantity == null || quantity < t.min_line_item_quantity) continue;
      if (!best || t.min_line_item_quantity > best.min_line_item_quantity) best = t;
    }
    return best;
  }

  function computeSubtotal() {
    var cart;
    return getJSON('/cart.js')
      .then(function (c) {
        cart = c;
        state.token = c.token || null;
        if (!c.items.length) return null;
        var ids = c.items.map(function (i) { return i.product_id; });
        return Promise.all([loadSettings(), loadCollectionIds(ids)]);
      })
      .then(function (both) {
        if (!both) return 0;
        var groups = (both[0] && both[0].discount_groups) || [];
        var collections = both[1] || {};
        var subtotal = 0;

        cart.items.forEach(function (item) {
          var unit = item.original_price != null ? item.original_price : item.price;
          var lineTotal = item.original_line_price != null ? item.original_line_price : unit * item.quantity;
          var lineCollections = collections[item.product_id] || [];
          var best = 0;

          // multiple_discount_group_behavior is apply_greatest_discount: groups
          // compete per line rather than stacking.
          groups.forEach(function (group) {
            if (!groupMatchesLine(group, lineCollections)) return;
            var tier = bestTierFor(group, item.quantity);
            if (!tier || tier.discount_type !== 'percentage') return;
            var amount = discountCents(unit, item.quantity, tier.discount_percent);
            if (amount > best) best = amount;
          });

          subtotal += Math.max(0, lineTotal - best);
        });

        return subtotal;
      });
  }

  function errorHost() {
    return document.querySelector('.qb-discount-code-inputs');
  }

  function showError() {
    var host = errorHost();
    if (!host) return;
    clearError();
    var label = document.createElement('label');
    label.className = ERROR_CLASS;
    label.textContent = MESSAGE;
    host.appendChild(label);
    var field = document.querySelector(CODE_INPUT);
    if (field) field.classList.add('qb-discount-code-error');
  }

  function clearError() {
    var existing = document.querySelectorAll('.promo-cap-error');
    for (var i = 0; i < existing.length; i++) existing[i].parentNode.removeChild(existing[i]);
  }

  /*
   * A code already accepted by BDN is stored against the cart token on its
   * server, so it survives the cart growing past the cap. Whenever the cart
   * changes we re-check and strip it, which is also what keeps the promotional
   * discount itself from dragging an ineligible order back under the line.
   */
  function enforceAppliedCode() {
    if (state.enforcing || !state.token) return;
    if (state.subtotal === null || state.subtotal <= CAP_CENTS) return;
    state.enforcing = true;
    getJSON(bdnHost() + '/cart/applied_discount_code/' + state.token)
      .then(function (applied) {
        if (!applied || !applied.is_valid) return null;
        // The token carries a "?key=" suffix; it must be percent-encoded or BDN's
        // remove route misses the record and silently reports nothing deleted.
        return getJSON(bdnHost() + '/cart/remove_discount_code/' + encodeURIComponent(state.token)).then(function () {
          if (window.qb && window.qb.datastore) window.qb.datastore.discount_codes = [];
          if (window.qb) window.qb.discountCodeApplied = false;
          var tag = document.querySelector('.qb-discount-code-tag');
          if (tag) tag.textContent = '';
          var log = document.querySelector('.qb-discount-code-log');
          if (log) log.textContent = '';
          var inputs = document.querySelectorAll('.qb-discount-code-inputs > input');
          for (var i = 0; i < inputs.length; i++) inputs[i].disabled = false;
          showError();
        });
      })
      .catch(function () { /* leave the cart alone if BDN is unreachable */ })
      .then(function () { state.enforcing = false; });
  }

  function refresh() {
    return computeSubtotal()
      .then(function (cents) {
        state.subtotal = cents;
        if (cents <= CAP_CENTS) clearError();
        enforceAppliedCode();
        return cents;
      })
      .catch(function () { state.subtotal = null; });
  }

  function overCap() {
    return state.subtotal !== null && state.subtotal > CAP_CENTS;
  }

  function intercept(event) {
    if (!overCap()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showError();
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest(APPLY_BUTTON)) return;
    intercept(e);
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    if (!e.target || !e.target.matches || !e.target.matches(CODE_INPUT)) return;
    intercept(e);
  }, true);

  // Cart mutations go through both fetch and jQuery XHR in this theme, so watch
  // both and re-derive the subtotal after each one settles.
  var CART_WRITE = /\/cart\/(add|change|update|clear)/;

  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = nativeFetch.apply(this, arguments);
      if (CART_WRITE.test(url)) {
        result.then(function () { setTimeout(refresh, 150); }, function () {});
      }
      return result;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__promoCapUrl = url;
    return nativeOpen.apply(this, arguments);
  };

  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    xhr.addEventListener('load', function () {
      if (CART_WRITE.test(xhr.__promoCapUrl || '')) setTimeout(refresh, 150);
    });
    return nativeSend.apply(this, arguments);
  };

  window.addEventListener('pageshow', refresh);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  // Exposed so the acceptance tests can be checked against live carts.
  window.promoCodeCap = { state: state, refresh: refresh, compute: computeSubtotal, capCents: CAP_CENTS };
})();
